'use no memo';

import type { Table as TableT } from '@tanstack/table-core';
import type { MouseEvent } from 'react';

import { cn } from '@repo/lib/cn';
import { flexRender } from '@tanstack/react-table';

import TanstackTableEmpty from '@/components/features/manage/tanstack-table/TanstackTableEmpty';
import { TableCell, TableRow } from '@/components/ui/table';
import { useMediaQuery } from '@/hooks/media-query';

interface TanstackTableRowsProps<TData> {
  table: TableT<TData>;
  colSpan: number;
  onRowClick?: (event: MouseEvent, row: ReturnType<TableT<TData>['getRowModel']>['rows'][number], rowIndex: number) => void;
}

export default function TanstackTableRows<TData>(tanstackTableRowsProps: TanstackTableRowsProps<TData>) {
  const { table, colSpan, onRowClick } = tanstackTableRowsProps;
  const breakpoint = useMediaQuery('md');
  const tableContentLength = table.getRowModel().rows.length;
  if (!tableContentLength) {
    return <TanstackTableEmpty colSpan={colSpan} />;
  }
  return table.getRowModel().rows.map((row, rowIndex) => {
    if (breakpoint) {
      return (
        <TableRow
          key={row.id}
          data-state={row.getIsSelected() && 'selected'}
          onClick={(e) => onRowClick?.(e, row, rowIndex)}
          onMouseDown={(e) => {
            // Prevent text selection only when Shift is pressed
            if (e.shiftKey) {
              e.preventDefault();
            }
          }}
          className={onRowClick ? 'cursor-pointer' : undefined}
        >
          {row.getVisibleCells().map((cell) => {
            return (
              <TableCell key={cell.id} className={'px-3 py-1'}>
                {flexRender(cell.column.columnDef.cell, cell.getContext())}
              </TableCell>
            );
          })}
        </TableRow>
      );
    }
    return (
      <div key={row.id} className={'border-border-primary bg-surface-primary flex w-full flex-col gap-0.5 rounded-xl border py-4'}>
        {row.getVisibleCells().map((cell, index, cells) => {
          const isLast = index === cells.length - 1;
          return (
            <div key={cell.id} className={cn('flex min-h-7 items-center justify-between gap-3 px-4', isLast ? 'border-border-primary mt-4 border-t px-4 pt-4' : null)}>
              <span className={'text-text-tertiary w-fit shrink-0 text-sm'}>{cell.getContext().column.columnDef.meta as string}</span>
              <div className={'shrink truncate'}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</div>
            </div>
          );
        })}
      </div>
    );
  });
}
